import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import { parseDate } from 'utils/datetime';
import { localizeDateTime, DateFormat } from 'utils/l10n/datetime';
import { isDefined } from 'utils/tsHelpers';
import { FieldType, DemographicInsightPresentationMode } from 'modules/domains/segmentation';
import { ContactProperty } from 'modules/domains/user';
import { useUserProfileContactProperties } from 'modules/userProfiles/hooks';
import { CustomDemographicContext } from './CustomDemographicContext';
import {
  useAddCustomDemographicInsight,
  useDeleteCustomDemographicInsight,
  useCustomDemographicInsightsQuery,
} from '../../hooks';
import { CustomDemographicInsight, ProfileQuery, AudienceCount } from '../../types';
import { MISSING_VALUE, OTHER_VALUE } from '../../constants';
import { useProfileQueryContext } from './ProfileQueryContext';

/** opting to using switch case just in CASE (pun intended) more data manipulation needs to be made
 *  based on FieldType
 */
const reformatCustomInsights = (
  insights: AudienceCount[],
  fieldType?: FieldType,
): AudienceCount[] => {
  /** an undefined value means that it hasn't received the actual value from the rest api and is waiting on cache */
  if (!isDefined(insights)) return insights;

  switch (fieldType) {
    case FieldType.DATE:
      return insights.map(({ name, count }) => {
        const isDate = ![MISSING_VALUE, OTHER_VALUE].includes(name); // Skip date formatting on catch-all keys like "other"
        const formattedName = isDate
          ? localizeDateTime(parseDate(Number(name)), {
              dateStyle: DateFormat.Numeric,
            })
          : name;
        return {
          name: formattedName,
          count,
        };
      });
    default:
      return insights;
  }
};

export const CustomDemographicContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { refetchProfile, profile, isProfileLoading } = useProfileQueryContext();
  const { data: fieldMappings } = useUserProfileContactProperties();
  const {
    addCustomDemographicInsight,
    isLoading: isAddingInsight,
    isSuccess: customInsightAdded,
  } = useAddCustomDemographicInsight();
  const {
    deleteCustomDemographicInsight,
    isLoading: isDeletingInsight,
    isSuccess: customInsightDeleted,
  } = useDeleteCustomDemographicInsight();
  const {
    data: { maxDemographicInsightSettingsForProject, settings } = {},
    refetch: refetchCustomInsights,
    isLoading: isFetchingCustomInsights,
  } = useCustomDemographicInsightsQuery();

  const [cachedCustomDemograhpicInsights, updateCustomDemographicInsights] = useState<
    CustomDemographicInsight[]
  >([]);

  const reformatedInsightSettings = useMemo(
    () =>
      settings
        ? settings.map(({ contactProperty, presentationMode }) => ({
            contactProperty: contactProperty as keyof ProfileQuery,
            presentationMode,
          }))
        : [],
    [settings],
  );

  useAsync(async () => {
    if (customInsightAdded || customInsightDeleted) {
      await refetchCustomInsights();
      await refetchProfile();
    }
  }, [customInsightDeleted, customInsightAdded]);

  useEffect(() => {
    if (!isFetchingCustomInsights && !isProfileLoading) {
      updateCustomDemographicInsights(
        reformatedInsightSettings
          .map(({ contactProperty, presentationMode }) => {
            const contactPropertyType = fieldMappings.find(
              ({ name }) => name === contactProperty,
            )?.type;
            return {
              contactProperty,
              values: reformatCustomInsights(
                profile[contactProperty] as AudienceCount[],
                contactPropertyType,
              ),
              mode: presentationMode,
            };
          })
          .reverse(),
      );
    }
  }, [
    // fieldMappings,
    isFetchingCustomInsights,
    isProfileLoading,
    profile,
    reformatedInsightSettings,
  ]);

  const updatedContactProperties: ContactProperty[] = useMemo(() => {
    const removedNonPrimitveFields = fieldMappings.filter(
      ({ type }) => ![FieldType.OBJECT, FieldType.NESTED].includes(type),
    );

    return removedNonPrimitveFields.filter(({ name }) =>
      cachedCustomDemograhpicInsights.length > 0
        ? !!cachedCustomDemograhpicInsights.every(({ contactProperty }) => contactProperty !== name)
        : removedNonPrimitveFields,
    );
  }, [cachedCustomDemograhpicInsights, fieldMappings]);

  const addDemographicContactProperty = useCallback(
    (selectedContactProperty: string, mode: DemographicInsightPresentationMode) => {
      addCustomDemographicInsight(selectedContactProperty, mode);
    },
    [addCustomDemographicInsight],
  );

  const deleteDemographicContactProperty = useCallback(
    (selectedContactProperty: string) => {
      deleteCustomDemographicInsight(selectedContactProperty);
      updateCustomDemographicInsights((previous) =>
        previous.filter((insight) => {
          const title = Object.keys(insight)[0];
          return title !== selectedContactProperty;
        }),
      );
    },
    [deleteCustomDemographicInsight],
  );

  const value = {
    addDemographicContactProperty,
    deleteDemographicContactProperty,
    cachedCustomDemograhpicInsights,
    contactProperties: updatedContactProperties,
    maxDemographicInsightSettingsForProject: maxDemographicInsightSettingsForProject ?? 0,
    isCustomDemographicInsightLoading:
      isDeletingInsight || isAddingInsight || isFetchingCustomInsights,
  };

  return (
    <CustomDemographicContext.Provider value={value}>{children}</CustomDemographicContext.Provider>
  );
};
